import type { ConnectedChat } from './api'
import { dateOnly } from './format'

/**
 * The line under a connected chat's name, in Organization settings and on
 * the account page. The name is whatever the chat called itself; the host
 * its connection went to is who holds it, so both lists name that host. A
 * chat connected before the server recorded the host has none, and the line
 * then says only when. A connection ends 90 days after it was allowed,
 * whether or not the chat is used, so the line says when.
 *
 * Organization settings says a chat acts as the person who connected it, so
 * each of its rows names that person (D14). The person's own list has no
 * such name: every chat on it is theirs.
 */
export function connectedLine(
  chat: Pick<ConnectedChat, 'created_at' | 'redirect_host' | 'expires_at' | 'connected_by'>,
  organizationName?: string,
): string {
  const when = `${dateOnly(chat.created_at)}${chat.redirect_host ? `, to ${chat.redirect_host}` : ''}`
  const ends = Number.isNaN(Date.parse(chat.expires_at ?? '')) ? '' : `, ends ${dateOnly(chat.expires_at)}`
  const by = chat.connected_by?.trim()
  const connected = by ? `Connected by ${by} on ${when}` : `Connected ${when}`
  return `${organizationName ? `At ${organizationName}, ${connected.charAt(0).toLowerCase()}${connected.slice(1)}` : connected}${ends}`
}

/**
 * Whether a connected chat can still act. The server refuses a grant's token
 * and its refresh once its expires_at has passed, but still lists it until it
 * is revoked, so a list read as it came named a chat that had ended, with a
 * button to end it again, often beside the new grant the chat asked for when
 * it ended. A grant whose end cannot be read is shown, since hiding a chat
 * that may still act as somebody is the worse mistake.
 */
export function stillActs(chat: Pick<ConnectedChat, 'expires_at'>, now: number): boolean {
  const end = Date.parse(chat.expires_at ?? '')
  return Number.isNaN(end) || end > now
}
